/**
 * Removing what a tracker left behind, when its consent is withdrawn.
 *
 * Withdrawal has to stop the storage, not merely stop the reporting. A tag
 * already loaded into this page keeps its cookies unless they are deleted, and
 * a visitor who has just said no would find them still there — which is the
 * difference between an honest withdrawal and a gesture.
 *
 * A cookie is deleted by setting it again with a past expiry, and only for the
 * exact domain and path it was written with. These tags write on the
 * registrable domain, so the current host and its parent are both tried;
 * anything that does not match is simply not deleted and nothing breaks.
 */
export function clearCookies(matches: RegExp) {
  if (typeof document === 'undefined') return;
  const host = window.location.hostname;
  const parent = host.split('.').slice(-2).join('.');
  const domains = ['', host, `.${host}`, `.${parent}`];
  for (const entry of document.cookie.split(';')) {
    const name = entry.split('=')[0]?.trim();
    if (!name || !matches.test(name)) continue;
    for (const domain of domains) {
      document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${
        domain ? `; domain=${domain}` : ''
      }`;
    }
  }
}

/** Google Analytics 4. */
export const GA_COOKIES = /^(_ga|_gid|_gat)/;

/** Hotjar. It also keeps entries in sessionStorage, cleared alongside. */
export const HOTJAR_COOKIES = /^(_hj)/;

/** The Meta pixel's first-party cookie. `fr` is Meta's own, set on
 *  facebook.com rather than here, and is not ours to remove. */
export const META_COOKIES = /^(_fbp|_fbc)/;

/** Hotjar stores as much in sessionStorage as it does in cookies, so a
 *  withdrawal that only cleared cookies would leave the recording state
 *  behind. */
export function clearHotjarStorage() {
  for (const store of [window.sessionStorage, window.localStorage]) {
    try {
      for (const key of Object.keys(store)) {
        if (key.startsWith('_hj')) store.removeItem(key);
      }
    } catch {
      /* Storage blocked. There is then nothing stored to clear. */
    }
  }
}
